import React, { memo, useCallback } from "react";

import Grid from "@material-ui/core/Grid";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  ZoomyDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import MessageBox from "components/MessageBox";

import { ApiModel } from "constants/loading";

import {
  getBusinessById,
  getContextByModalType,
  getPaymentById,
  selectActiveLivestockAgentDeploymentMasterBusinessId,
} from "selectors";

import {
  computeInitialValues,
  ModalContextWithSnapshot,
} from "sagas/modals/util";
import { ModalTypes } from "constants/navigation";
import { PaymentMethod, BuyerPaymentSummaryData } from "constants/payments";
import { Box } from "@material-ui/core";
import { BoldText, Text } from "components/Text";
import { Row } from "components/Layout";
import { getBuyerBalanceSummaryByBuyerId } from "selectors/clearingSaleInvoice";
import { DeleteButton } from "components/Form";
import {
  PaymentFormValues,
  promptDeletePaymentFromModal,
  submitPaymentForm,
} from "actions/modals/payment";
import { formatDollarsFromCents } from "lib";
import { EditPaymentForm } from "./EditPaymentForm";
import { Footer } from "./Footer";
import { ValidationSchema } from "./validationSchema";
import { PurchaseSummaryTable } from "./SummaryTable";

interface EditPaymentModalModalTitleProps {
  businessId: string;
  isEdit: boolean;
  onClose: () => void;
}

function EditPaymentModalModalTitleComponent(
  props: EditPaymentModalModalTitleProps,
) {
  const { businessId, isEdit, onClose } = props;

  const businessName =
    useSelector(state => getBusinessById(businessId)(state)?.name) || "";

  return (
    <DialogTitle onClose={onClose}>
      {isEdit ? "Edit" : "Add"} payment from {businessName}
    </DialogTitle>
  );
}

const EditPaymentModalModalTitle = memo(EditPaymentModalModalTitleComponent);

type FormValues = PaymentFormValues;

type ModalContext = {
  paymentId: string;
  fromBusinessId: string;
} & ModalContextWithSnapshot<FormValues>;

interface EditPaymentModalProps {
  onClose: () => void;
}

export function EditPaymentModal(props: EditPaymentModalProps) {
  const { onClose } = props;
  const dispatch = useDispatch();
  const modalContext = useSelector<State, ModalContext>(
    getContextByModalType(ModalTypes.EditPayment),
  );

  const { paymentId, fromBusinessId } = modalContext;

  const payment = useSelector<State, Payment>(getPaymentById(paymentId));

  const deploymentMasterBusinessId: string | null = useSelector(
    selectActiveLivestockAgentDeploymentMasterBusinessId,
  );

  const isEdit = Boolean(paymentId);

  const onSubmitForm = useCallback(
    (formValues: FormValues) => {
      dispatch(submitPaymentForm(formValues, !isEdit));
    },
    [dispatch, isEdit],
  );

  const onClickDelete = useCallback(
    () => dispatch(promptDeletePaymentFromModal(paymentId)),
    [dispatch, paymentId],
  );

  const defaults: Partial<FormValues> = {
    fromBusinessId,
    // the current date as an ISO8601 date string
    paymentDate: new Date().toISOString().slice(0, 10),
    paymentMethod: PaymentMethod.CASH,
    toBusinessId: deploymentMasterBusinessId,
  };

  const initialValues = computeInitialValues(defaults, modalContext, payment);

  const buyerBalanceSummary = useSelector<State, BuyerPaymentSummaryData>(
    getBuyerBalanceSummaryByBuyerId(initialValues.fromBusinessId),
  );

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="sm" fullWidth>
      <WaitForSync
        requiredData={[
          ApiModel.BUSINESSES,
          ApiModel.BILLING_RUNS,
          ApiModel.BILLING_DOCUMENTS,
        ]}
      >
        <EditPaymentModalModalTitle
          onClose={onClose}
          businessId={initialValues.fromBusinessId}
          isEdit={Boolean(paymentId)}
        />
        {!deploymentMasterBusinessId ? (
          <MessageBox>Recipient&apos;s Business could not be found.</MessageBox>
        ) : (
          <Formik
            initialValues={initialValues}
            onSubmit={onSubmitForm}
            validationSchema={ValidationSchema}
          >
            <Form>
              <DialogContent dividers>
                <Box marginBottom={2}>
                  <Row gridGap={1}>
                    <Text fontSize="charlie">Due</Text>
                    <BoldText fontSize="charlie">
                      {formatDollarsFromCents(
                        buyerBalanceSummary.totals.dueCents,
                        { format: true },
                      )}
                    </BoldText>
                  </Row>
                  <PurchaseSummaryTable
                    businessId={initialValues.fromBusinessId}
                  />
                </Box>
                <Grid spacing={2} container>
                  <EditPaymentForm />
                </Grid>

                {isEdit && (
                  <DeleteButton
                    data-tour="delete"
                    type="button"
                    onClick={onClickDelete}
                  >
                    Delete
                  </DeleteButton>
                )}
              </DialogContent>
              <DialogActions>
                <Footer isEdit={isEdit} onClose={onClose} />
              </DialogActions>
            </Form>
          </Formik>
        )}
      </WaitForSync>
    </ZoomyDialog>
  );
}
